<script>
  import { writable } from "svelte/store";
  import {
    Background,
    ConnectionMode,
    MarkerType,
    SvelteFlow,
  } from "@xyflow/svelte";
  import "@xyflow/svelte/dist/style.css";
  import { dagre } from "dagre-d3";
  import domtoimage from "dom-to-image";
  import ContentSwitcher from "../../../../lib/components/ContentSwitcher.svelte";
  import ClusterNode from "../analysis/components/ClusterNode.svelte";
  import CaseIdPreviewNode from "./CaseIdPreviewNode.svelte";
  import CaseIdPreviewEdge from "./CaseIdPreviewEdge.svelte";
  import CaseIdViewportController from "./CaseIdViewportController.svelte";
  import { formatTimeDelta } from "../../../../lib/util/formatfunctions.js";

  export let nodesData = [];
  export let edgesData = [];
  export let minFrequency = 1;
  export let controlsBottom = "0.75rem";
  export let playbackNode = "";
  export let playbackFrom = "";
  export let playbackTo = "";
  export let playbackDuration = null;
  export let edgeMetric = 0;
  export let colorMetric = "none";
  export let appTypesByName = {};
  export let showMetricSwitcher = true;
  export let preserveMissingValues = false;

  const taskWidth = 390;
  const taskHeight = 78;
  const expandedTaskHeight = 132;
  const endpointWidth = 90;
  const endpointHeight = 48;
  const nodes = writable([]);
  const edges = writable([]);
  const nodeTypes = { preview: CaseIdPreviewNode, cluster: ClusterNode };
  const edgeTypes = { preview: CaseIdPreviewEdge };
  let expandedNodes = new Set();
  let baseNodes = [];
  let baseEdges = [];
  let layoutInputNodes = [];
  let previewWrapper;
  let viewportController;

  export async function incrementZoom() {
    return viewportController?.incrementZoom();
  }

  export async function decrementZoom() {
    return viewportController?.decrementZoom();
  }

  export function centerGraph() {
    return viewportController?.centerGraph();
  }

  function hasNumericValue(value) {
    return value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value));
  }

  export async function downloadPNG(filename = "object-explorer.png") {
    const element = previewWrapper?.querySelector("#case-id-preview-flow");
    if (!element) return;
    if (document.fonts?.ready) await document.fonts.ready;
    const blob = await domtoimage.toBlob(element, {
      bgcolor: "#f7f8fa",
      width: element.clientWidth,
      height: element.clientHeight,
      filter: (node) => !node?.classList?.contains("svelte-flow__controls"),
    });
    if (!blob) throw new Error("Could not create PNG image");
    const pngUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = pngUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(pngUrl), 1000);
  }

  $: rebuildGraph(
    nodesData,
    edgesData,
    edgeMetric,
    minFrequency,
    playbackNode,
    playbackFrom,
    playbackTo,
    playbackDuration,
    colorMetric,
    appTypesByName,
  );

  function rebuildGraph(sourceNodes, sourceEdges, metric, minimum, activeNode, activeFrom, activeTo, activeDuration, highlightMetric, appTypes) {
    const allNodes = Array.isArray(sourceNodes) ? sourceNodes : [];
    const inputEdges = Array.isArray(sourceEdges)
      ? sourceEdges.filter(
        (edge) => (
          preserveMissingValues && !hasNumericValue(edge.value)
          || hasNumericValue(edge.value) && Number(edge.value) >= Number(minimum || 1)
        ),
      )
      : [];
    const totalStarts = inputEdges
      .filter((edge) => edge.from === "Start")
      .reduce((sum, edge) => sum + Number(edge.value || 0), 0);
    const connectedIds = new Set(inputEdges.flatMap((edge) => [edge.from, edge.to]));
    const connectedParents = new Set(
      allNodes
        .filter((node) => connectedIds.has(node.id) && node.parentNode)
        .map((node) => node.parentNode),
    );
    const inputNodes = allNodes.filter(
      (node) => connectedIds.has(node.id) || connectedParents.has(node.id),
    );
    const parentIds = new Set(
      inputNodes.filter((node) => node.parentNode).map((node) => node.parentNode),
    );
    const graphNodes = inputNodes.filter((node) => !parentIds.has(node.id));
    const clusterNodes = inputNodes.filter((node) => parentIds.has(node.id));
    const nodeIds = new Set(graphNodes.map((node) => node.id));
    const graph = new dagre.graphlib.Graph({ compound: true });

    graph.setGraph({ rankdir: "TB", nodesep: 120, ranksep: 110, marginx: 50, marginy: 50 });
    graph.setDefaultEdgeLabel(() => ({}));

    graphNodes.forEach((node) => {
      const endpoint = node.id === "Start" || node.id === "End";
      graph.setNode(node.id, {
        width: endpoint ? endpointWidth : taskWidth,
        height: endpoint ? endpointHeight : taskHeight,
      });
    });
    clusterNodes.forEach((cluster) => graph.setNode(cluster.id, { label: cluster.label }));
    graphNodes.forEach((node) => {
      if (node.parentNode && parentIds.has(node.parentNode)) {
        graph.setParent(node.id, node.parentNode);
      }
    });
    inputEdges.forEach((edge) => {
      if (nodeIds.has(edge.from) && nodeIds.has(edge.to)) {
        graph.setEdge(edge.from, edge.to, { edge });
      }
    });
    dagre.layout(graph);

    const positionedClusters = clusterNodes.map((cluster) => {
      const position = graph.node(cluster.id);
      return {
        id: cluster.id,
        type: "cluster",
        data: { label: cluster.label },
        position: { x: position.x - position.width / 2, y: position.y - position.height / 2 },
        width: position.width,
        height: position.height,
        style: `width:${position.width}px;height:${position.height}px;`,
        selectable: false,
        draggable: false,
        zIndex: -1,
      };
    });

    const positionedNodes = [
      ...positionedClusters,
      ...graphNodes.map((node) => {
        const position = graph.node(node.id);
        const endpoint = node.id === "Start" || node.id === "End";
        return {
          id: node.id,
          type: "preview",
          data: {
            label: node.label,
            metrics: node.metrics || {},
            startRate: totalStarts
              ? inputEdges
                .filter((edge) => edge.from === "Start" && edge.to === node.id)
                .reduce((sum, edge) => sum + Number(edge.value || 0), 0) / totalStarts
              : null,
            isStart: node.id === "Start",
            isEnd: node.id === "End",
            expanded: false,
            onToggle: toggleNode,
            playbackActive: node.id === activeNode,
            highlightColor: getHighlightColor(node, highlightMetric, graphNodes, appTypes),
            caseIdActivities: node.caseIdActivities || [],
          },
          position: {
            x: position.x - (endpoint ? endpointWidth : taskWidth) / 2,
            y: position.y - position.height / 2,
          },
          draggable: false,
          selectable: false,
        };
      }),
    ];

    const positionedEdges = graph.edges().map((descriptor) => {
      const data = graph.edge(descriptor);
      const edge = data.edge;
      const playbackActive = descriptor.v === activeFrom && descriptor.w === activeTo;
      return {
        id: `${descriptor.v}-${descriptor.w}`,
        source: descriptor.v,
        target: descriptor.w,
        type: "preview",
        markerEnd: {
          type: MarkerType.ArrowClosed,
          width: 18,
          height: 18,
          color: playbackActive ? "#ff5f1f" : "#626262",
        },
        data: {
          points: data.points,
          playbackActive,
          label: metric === 0
            ? hasNumericValue(edge.value) ? edge.value : "–"
            : playbackActive && hasNumericValue(activeDuration)
              ? formatTimeDelta(activeDuration)
              : Number(edge.averageDurationSeconds) > 0
              ? formatTimeDelta(edge.averageDurationSeconds)
              : "–",
        },
      };
    });

    baseNodes = positionedNodes;
    baseEdges = positionedEdges;
    layoutInputNodes = inputNodes;
    applyExpansionLayout(baseNodes, baseEdges, layoutInputNodes, expandedNodes);
  }

  function metricValue(node, metric) {
    if (metric === "text") return Number(node.metrics?.text_share);
    if (metric === "copy") return Number(node.metrics?.copy_paste_time_share);
    if (metric === "time") return Number(node.metrics?.time_per_case);
    return null;
  }

  function getHighlightColor(node, metric, allNodes, appTypes) {
    if (["Start", "End"].includes(node.id) || metric === "none") return "#f2f2f2";
    if (metric === "appType") {
      const app = Object.entries(node.metrics?.apps || {}).sort((a, b) => Number(b[1]) - Number(a[1]))[0]?.[0];
      const type = appTypes[String(app || "").trim().toLowerCase()];
      if (type === "main") return "#b7e3bd";
      if (type === "support" || type === "supportive") return "#e5e8a8";
      if (type === "communication") return "#abcfe8";
      if (type === "unwanted") return "#e6a7ad";
      return "#f2f2f2";
    }
    const value = metricValue(node, metric);
    const maxValue = Math.max(
      ...allNodes.map((item) => metricValue(item, metric)).filter((item) => Number.isFinite(item) && item > 0),
      0,
    );
    if (!Number.isFinite(value) || !maxValue) return "#f2f2f2";
    const share = value / maxValue;
    if (share >= 0.66) return "#e6a7ad";
    if (share >= 0.33) return "#e5e8a8";
    return "#b7e3bd";
  }

  function applyExpansionLayout(positionedNodes, positionedEdges, inputNodes, expanded) {
    const delta = expandedTaskHeight - taskHeight;
    const parentById = Object.fromEntries(inputNodes.map((node) => [node.id, node.parentNode]));
    const originalById = Object.fromEntries(positionedNodes.map((node) => [node.id, node]));
    const expandedEntries = [...expanded]
      .map((id) => ({ id, bottomY: originalById[id]?.position.y + taskHeight, delta }))
      .filter((entry) => Number.isFinite(entry.bottomY))
      .sort((a, b) => a.bottomY - b.bottomY);

    function shiftAt(y, selfId) {
      return expandedEntries.reduce(
        (shift, entry) => shift + (
          selfId !== entry.id && y > originalById[entry.id].position.y ? entry.delta : 0
        ),
        0,
      );
    }

    function nodeShift(nodeId) {
      const original = originalById[nodeId];
      return original ? shiftAt(original.position.y, nodeId) : 0;
    }

    nodes.set(positionedNodes.map((node) => {
      const isCluster = node.type === "cluster";
      const ownExpansion = isCluster
        ? expandedEntries.filter((entry) => parentById[entry.id] === node.id).length * delta
        : 0;
      const shift = shiftAt(node.position.y, node.id);
      const height = isCluster ? node.height + ownExpansion : node.height;
      return {
        ...node,
        position: isCluster
          ? { ...node.position, y: node.position.y + shift }
          : node.position,
        ...(isCluster ? { height, style: `width:${node.width}px;height:${height}px;` } : {}),
        data: node.type === "preview"
          ? { ...node.data, expanded: expanded.has(node.id), visualShift: shift }
          : node.data,
      };
    }));

    const shiftedEdges = positionedEdges.map((edge) => {
      const originalPoints = edge.data.points;
      const sourceExpansion = expandedEntries.find((entry) => entry.id === edge.source);
      const targetExpansion = expandedEntries.find((entry) => entry.id === edge.target);
      let sourceShift = nodeShift(edge.source);
      if (sourceExpansion && originalPoints[0]?.y >= sourceExpansion.bottomY - 2) {
        sourceShift += sourceExpansion.delta;
      }
      let targetShift = nodeShift(edge.target);
      const lastPoint = originalPoints[originalPoints.length - 1];
      if (targetExpansion && lastPoint?.y >= targetExpansion.bottomY - 2) {
        targetShift += targetExpansion.delta;
      }
      const points = originalPoints.map((point, index) => {
        let shift = 0;
        if (index === 0) shift = sourceShift;
        else if (index === originalPoints.length - 1) shift = targetShift;
        else {
          shift = expandedEntries.reduce(
            (sum, entry) => sum + (point.y > entry.bottomY ? entry.delta : 0),
            0,
          );
        }
        return shift ? { ...point, y: point.y + shift } : point;
      });
      return { ...edge, data: { ...edge.data, points } };
    });
    edges.set(placeEdgeLabels(shiftedEdges));
  }

  function placeEdgeLabels(positionedEdges) {
    const occupied = [];
    return positionedEdges.map((edge) => {
      const label = String(edge.data.label ?? "");
      const width = Math.max(58, label.length * 7 + 20);
      const height = 30;
      const candidates = [0.5, 0.38, 0.62, 0.26, 0.74]
        .map((fraction) => pointAlongPath(edge.data.points, fraction));
      const position = candidates.find((candidate) => !occupied.some((box) => (
        Math.abs(candidate.x - box.x) < (width + box.width) / 2 + 6
        && Math.abs(candidate.y - box.y) < (height + box.height) / 2 + 6
      ))) || candidates[0];
      occupied.push({ ...position, width, height });
      return { ...edge, data: { ...edge.data, labelPosition: position } };
    });
  }

  function pointAlongPath(points, fraction) {
    if (!points?.length) return { x: 0, y: 0 };
    if (points.length === 1) return points[0];
    const lengths = points.slice(1).map((point, index) => (
      Math.hypot(point.x - points[index].x, point.y - points[index].y)
    ));
    const target = lengths.reduce((sum, length) => sum + length, 0) * fraction;
    let covered = 0;
    for (let index = 0; index < lengths.length; index += 1) {
      if (covered + lengths[index] >= target) {
        const share = lengths[index] ? (target - covered) / lengths[index] : 0;
        return {
          x: points[index].x + (points[index + 1].x - points[index].x) * share,
          y: points[index].y + (points[index + 1].y - points[index].y) * share,
        };
      }
      covered += lengths[index];
    }
    return points[points.length - 1];
  }

  function toggleNode(nodeId) {
    if (expandedNodes.has(nodeId)) expandedNodes.delete(nodeId);
    else expandedNodes.add(nodeId);
    expandedNodes = new Set(expandedNodes);
    applyExpansionLayout(baseNodes, baseEdges, layoutInputNodes, expandedNodes);
  }

</script>

<div bind:this={previewWrapper} class="preview-wrapper" style={`--controls-bottom:${controlsBottom}`}>
  {#if showMetricSwitcher}
    <div class="metric-switcher">
      <ContentSwitcher options={["Count", "Avg. Throughput Time"]} bind:selected={edgeMetric} fitContent />
    </div>
  {/if}
  <SvelteFlow
    id="case-id-preview-flow"
    {nodes}
    {edges}
    {nodeTypes}
    {edgeTypes}
    fitView
    fitViewOptions={{ padding: 0.18, maxZoom: 1 }}
    minZoom={0.1}
    maxZoom={2.5}
    nodesDraggable={false}
    nodesConnectable={false}
    elementsSelectable={false}
    panOnDrag={true}
    panOnScroll={false}
    zoomOnScroll={true}
    zoomOnPinch={true}
    zoomOnDoubleClick={false}
    preventScrolling={true}
    connectionMode={ConnectionMode.Loose}
  >
    <CaseIdViewportController bind:this={viewportController} />
    <Background gap={24} size={1} color="#e0e0e0" />
  </SvelteFlow>
</div>

<style>
  .preview-wrapper {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
  }

  .metric-switcher {
    position: absolute;
    top: 0.75rem;
    left: 0.75rem;
    z-index: 10;
  }

  :global(#case-id-preview-flow) {
    width: 100%;
    height: 100%;
    background: var(--pax-white, #fff);
  }

  :global(#case-id-preview-flow .svelte-flow__controls) {
    margin: 0 0.75rem var(--controls-bottom) 0;
    overflow: hidden;
    border: 0;
    border-radius: 0.5rem;
    background: var(--pax-black);
    box-shadow: none;
  }

  :global(#case-id-preview-flow .svelte-flow__controls-button) {
    width: 2.5rem;
    height: 2.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 2.5rem;
    padding: 0;
    border-bottom: 0;
    border-right: 1px solid #3b3b3f;
    background: var(--pax-black);
    color: #fff;
    fill: #fff;
  }

  :global(#case-id-preview-flow .svelte-flow__controls-button svg) {
    width: 1rem;
    height: 1rem;
    max-width: none;
    fill: currentColor;
  }

  :global(#case-id-preview-flow .svelte-flow__controls-button svg path),
  :global(#case-id-preview-flow .svelte-flow__controls-button svg rect) {
    fill: currentColor;
    stroke: currentColor;
  }

  :global(#case-id-preview-flow .svelte-flow__controls-button:last-child) {
    border-right: 0;
  }

  :global(#case-id-preview-flow .svelte-flow__controls-button:hover) {
    background: var(--pax-orange);
  }

  :global(#case-id-preview-flow .svelte-flow__node-preview) {
    z-index: 3 !important;
  }

  :global(#case-id-preview-flow .svelte-flow__nodes) {
    z-index: 3;
  }

  :global(#case-id-preview-flow .svelte-flow__node-cluster) {
    z-index: 0 !important;
  }

  :global(#case-id-preview-flow .svelte-flow__node-cluster .cluster-node) {
    background: transparent;
  }

  :global(#case-id-preview-flow .svelte-flow__attribution) {
    display: none;
  }
</style>
